import { CardContent } from "@/components/ui/card";
import FindOneTimeSheetLog, {
    TimeSheetDetails,
} from "@/v2/api/time-sheet/FindOneTimeSheetLog";
import AttendanceTable from "@/v2/components/pages/time-sheet/AttendanceTable";
import PrintTimeSheet from "@/v2/components/pages/time-sheet/Print";
import TimeSheetStatusActionBtn from "@/v2/components/pages/time-sheet/TimeSheetStatusActionBtn";
import TimeSheetStatusBadge from "@/v2/components/pages/time-sheet/TimeSheetStatusBadge";
import LoadingAnimation from "@/v2/components/ui/LoadingAnimation";
import useTimeSheetRoles from "@/v2/hooks/useTimeSheetRoles";
import ViewLayout from "@/v2/layout/View";
import { useQuery } from "@tanstack/react-query";
import {
    BriefcaseBusinessIcon,
    Building2,
    HardHatIcon,
    ScrollTextIcon,
    Users,
} from "lucide-react";
import { useMemo } from "react";
import { useParams } from "react-router-dom";

const NavButtons = ({
    data,
    rejectAccess = false,
    transitionAccess = false,
}: {
    data: TimeSheetDetails;
    rejectAccess: boolean;
    transitionAccess: boolean;
}) => {
    return (
        <>
            <TimeSheetStatusBadge status={data?.status} />

            <PrintTimeSheet timeSheetId={data.id} status={data?.status} />

            {data?.status !== "APPROVED" && (
                <>
                    {rejectAccess && (
                        <TimeSheetStatusActionBtn
                            timeSheetId={data.id}
                            timeSheetDetails={data}
                            getDeductionData={() => ({
                                other_deductions: data.other_deductions,
                                deduction_note: data.deduction_note,
                            })}
                            status="REJECTED"
                        />
                    )}

                    {transitionAccess && (
                        <TimeSheetStatusActionBtn
                            timeSheetId={data.id}
                            timeSheetDetails={data}
                            getDeductionData={() => ({
                                other_deductions: data.other_deductions,
                                deduction_note: data.deduction_note,
                            })}
                            status={
                                data.status === "DRAFTED" ||
                                data.status === "REJECTED"
                                    ? "TO_BE_VERIFIED"
                                    : data.status === "TO_BE_VERIFIED"
                                      ? "VERIFIED"
                                      : "APPROVED"
                            }
                        />
                    )}
                </>
            )}
        </>
    );
};

export default function ViewOneTimeSheet() {
    const { timeSheetId } = useParams();

    const { canReject, canEditDraft, canVerify, canApprove } =
        useTimeSheetRoles();

    const query = useQuery({
        queryKey: ["time-sheets", timeSheetId],
        queryFn: () =>
            FindOneTimeSheetLog({
                timeSheetId: timeSheetId || "",
            }),
        enabled: !!timeSheetId,
    });

    const calculations = useMemo(() => {
        if (!query.data?.logs || !query.data?.details) {
            return {
                totalHours: 0,
                totalAmount: 0,
                deductionAmount: 0,
                otherDeductions: 0,
                grandTotal: 0,
            };
        }

        const logs = query.data.logs;
        const project = query.data.details.project;

        // Calculate total hours and total amount
        const totalHours = logs.reduce(
            (sum, entry) => sum + entry.total_hours,
            0,
        );

        const totalAmount = logs.reduce(
            (sum, entry) => sum + entry.total_hours * entry.hourly_rate,
            0,
        );

        // Calculate deduction amount (only if project.deduction is true)
        let deductionAmount = 0;
        if (project?.deduction && project?.deduction_amount) {
            deductionAmount = logs.reduce((sum, entry) => {
                const absentDays = entry.work_logs.filter(
                    (log) => log.status === "ABSENT",
                ).length;
                return sum + absentDays * (project.deduction_amount || 0);
            }, 0);
        }

        // Get other deductions
        const otherDeductions = query.data.details.other_deductions || 0;

        // Calculate grand total
        const grandTotal = totalAmount - deductionAmount - otherDeductions;

        return {
            totalHours,
            totalAmount,
            deductionAmount,
            otherDeductions,
            grandTotal,
        };
    }, [query.data]);

    if (query.isLoading) {
        return <LoadingAnimation />;
    }

    if (!query.data) return <p>Something went wrong</p>;

    const timeSheetData = query?.data;

    return (
        <ViewLayout
            title="Time Log"
            description="Employees attendance for the respective timelog"
            icon={HardHatIcon}
            buttons={
                <NavButtons
                    data={query?.data?.details}
                    rejectAccess={canReject}
                    transitionAccess={
                        query?.data?.details?.status === "DRAFTED" ||
                        query?.data?.details?.status === "REJECTED"
                            ? canEditDraft
                            : query?.data?.details?.status === "TO_BE_VERIFIED"
                              ? canVerify
                              : canApprove
                    }
                />
            }
        >
            <CardContent className="space-y-8">
                <div>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                        <ScrollTextIcon className="h-5 w-5" />
                        Time Sheet Details
                    </h2>
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <BriefcaseBusinessIcon className="mr-2 h-4 w-4" />
                                Project Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details.project.name || "N/A"}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building2 className="mr-2 h-4 w-4" />
                                Client Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details.client?.full_name ||
                                    "N/A"}
                            </p>
                        </div>
                        <div className="space-y-2">
                            <div className="flex items-center text-sm text-muted-foreground">
                                <Building2 className="mr-2 h-4 w-4" />
                                Supplier Name
                            </div>
                            <p className="text-base font-medium">
                                {timeSheetData?.details?.supplier?.full_name ||
                                    "N/A"}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Attendance Table */}
                <div>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                        <Users className="h-5 w-5" />
                        Employee Attendance
                    </h2>
                    <AttendanceTable
                        attendanceDetails={timeSheetData?.logs}
                        startDate={timeSheetData.details.start_date}
                        endDate={timeSheetData.details?.end_date}
                    />
                </div>

                {/* Financial Summary Section */}
                <div>
                    <h2 className="mb-4 flex items-center gap-2 text-xl font-semibold">
                        <ScrollTextIcon className="h-5 w-5" />
                        Financial Summary
                    </h2>
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-6">
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                            {/* Timesheet Summary Column */}
                            <div className="space-y-3 border-r pr-6">
                                <h3 className="mb-4 font-semibold text-gray-900">
                                    Timesheet Summary
                                </h3>
                                <div className="flex justify-between">
                                    <p className="text-gray-700">
                                        Total Hours:
                                    </p>
                                    <p className="font-bold text-gray-900">
                                        {calculations.totalHours.toFixed(2)}{" "}
                                        Hours
                                    </p>
                                </div>
                                <div className="flex justify-between">
                                    <p className="text-gray-700">
                                        Total Amount:
                                    </p>
                                    <p className="font-bold text-green-600">
                                        {calculations.totalAmount.toFixed(2)}{" "}
                                        SAR
                                    </p>
                                </div>
                                <div className="flex justify-between gap-1 border-t pt-3">
                                    <p className="text-gray-700">
                                        Deduction (Absents):
                                    </p>
                                    <p className="font-bold text-red-600">
                                        {timeSheetData?.details?.project
                                            ?.deduction
                                            ? `${calculations.deductionAmount.toFixed(
                                                  2,
                                              )} SAR`
                                            : "Not subjected to deduction"}
                                    </p>
                                </div>
                                {calculations.otherDeductions > 0 && (
                                    <div className="flex justify-between gap-1 border-t pt-3">
                                        <p className="text-gray-700">
                                            Other Deductions:
                                        </p>
                                        <p className="font-bold text-red-600">
                                            {calculations.otherDeductions.toFixed(
                                                2,
                                            )}{" "}
                                            SAR
                                        </p>
                                    </div>
                                )}
                                <div className="flex justify-between gap-1 border-t-2 border-gray-800 pt-3">
                                    <p className="text-lg font-bold text-gray-900">
                                        Grand Total:
                                    </p>
                                    <p className="text-lg font-bold text-blue-600">
                                        {calculations.grandTotal.toFixed(2)} SAR
                                    </p>
                                </div>
                            </div>

                            {/* Other Deductions Column */}
                            <div className="space-y-3 border-r pr-6">
                                <h3 className="mb-4 font-semibold text-gray-900">
                                    Other Deductions
                                </h3>
                                <div className="flex justify-between">
                                    <p className="text-gray-700">Amount:</p>
                                    <p className="font-bold text-red-600">
                                        {timeSheetData?.details?.other_deductions?.toFixed(
                                            2,
                                        ) || "0.00"}{" "}
                                        SAR
                                    </p>
                                </div>
                                <div className="border-t pt-3">
                                    <p className="mb-2 text-sm font-medium text-gray-700">
                                        Reason:
                                    </p>
                                    <p className="text-sm text-gray-600">
                                        {timeSheetData?.details
                                            ?.deduction_note || "N/A"}
                                    </p>
                                </div>
                            </div>

                            {/* Comment Column */}
                            <div className="space-y-3">
                                <h3 className="mb-4 font-semibold text-gray-900">
                                    Comment
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {timeSheetData?.details?.note || "N/A"}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </CardContent>
        </ViewLayout>
    );
}
